// Currency-aware amount formatting. INR keeps Indian digit grouping
// (₹1,00,000.00); everything else uses the currency's own symbol and decimals
// (e.g. $250.00, ¥5,000) via Intl.

const formatterCache = {};

const getFormatter = (iso) => {
  if (!formatterCache[iso]) {
    try {
      formatterCache[iso] = new Intl.NumberFormat(iso === "INR" ? "en-IN" : "en-US", {
        style: "currency",
        currency: iso,
      });
    } catch (e) {
      // Unknown ISO code: plain number with the code as prefix.
      formatterCache[iso] = {
        format: (n) =>
          `${iso} ${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        resolvedOptions: () => ({ maximumFractionDigits: 2 }),
      };
    }
  }
  return formatterCache[iso];
};

export const formatMoney = (amount, iso = "INR") =>
  getFormatter(iso || "INR").format(Number(amount || 0));

// Decimals the currency is shown with (0 for JPY, 2 for most).
export const currencyDecimals = (iso = "INR") =>
  Math.min(getFormatter(iso || "INR").resolvedOptions().maximumFractionDigits, 2);

// Short prefix for input fields: "₹", "$", "€", or the ISO code.
export const currencyPrefix = (iso = "INR") => {
  const part = (getFormatter(iso || "INR").formatToParts?.(0) || []).find(
    (p) => p.type === "currency"
  );
  return part?.value || iso || "INR";
};

// ISO code of a record that carries `currency_data` (payment links, invoices).
export const recordCurrency = (record) =>
  record?.currency_data?.iso_code || record?.currency_code || "INR";

// Grand total a customer is asked to pay, matching the backend: INR rounds to
// whole rupees, other currencies keep their minor units (USD 99.95 stays 99.95).
export const roundPayable = (total, iso = "INR") => {
  const value = Number(total || 0);
  if (!iso || iso === "INR") return Math.round(value);
  const factor = 10 ** currencyDecimals(iso);
  return Math.round(value * factor) / factor;
};
